import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  GoogleAdsCampaign,
  GoogleAdsCampaignMetrics,
  GoogleAdsIntegration,
  GoogleAdsCampaignType,
  GoogleAdsCampaignStatus,
} from '@/models/google-ads.model';

const GOOGLE_ADS_CAMPAIGNS_COLLECTION = 'googleAdsCampaigns';
const GOOGLE_ADS_INTEGRATIONS_COLLECTION = 'googleAdsIntegrations';

export class GoogleAdsService {
  // ========== Integration Management ==========

  static async createIntegration(integrationData: Partial<GoogleAdsIntegration>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      accountId: integrationData.accountId || '',
      accountName: integrationData.accountName || '',
      refreshToken: integrationData.refreshToken || null,
      accessToken: integrationData.accessToken || null,
      tokenExpiresAt: integrationData.tokenExpiresAt ? Timestamp.fromDate(integrationData.tokenExpiresAt) : null,
      isActive: integrationData.isActive ?? true,
      lastSyncedAt: integrationData.lastSyncedAt ? Timestamp.fromDate(integrationData.lastSyncedAt) : null,
      syncFrequency: integrationData.syncFrequency || 'daily',
      createdBy: integrationData.createdBy || '',
      createdAt: now,
      lastUpdated: now,
    };

    const docRef = await addDoc(collection(db, GOOGLE_ADS_INTEGRATIONS_COLLECTION), data);
    return docRef.id;
  }

  static async getIntegration(accountId: string): Promise<GoogleAdsIntegration | null> {
    const q = query(
      collection(db, GOOGLE_ADS_INTEGRATIONS_COLLECTION),
      where('accountId', '==', accountId),
      where('isActive', '==', true)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }
    return this.docToIntegration(snapshot.docs[0].id, snapshot.docs[0].data());
  }

  static async getAllIntegrations(): Promise<GoogleAdsIntegration[]> {
    try {
      console.log('[GoogleAdsService] Fetching all integrations...');
      const q = query(
        collection(db, GOOGLE_ADS_INTEGRATIONS_COLLECTION),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const integrations = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log('[GoogleAdsService] Integration doc:', {
          id: doc.id,
          accountId: data.accountId,
          accountName: data.accountName,
          isActive: data.isActive,
          createdBy: data.createdBy,
        });
        return this.docToIntegration(doc.id, data);
      });
      console.log('[GoogleAdsService] Total integrations found:', integrations.length);
      return integrations;
    } catch (error: any) {
      console.error('[GoogleAdsService] Error fetching integrations:', error);
      // If orderBy fails (missing index), try without it
      if (error.code === 'failed-precondition' && error.message?.includes('index')) {
        console.warn('[GoogleAdsService] Index missing, fetching without orderBy...');
        const q = query(
          collection(db, GOOGLE_ADS_INTEGRATIONS_COLLECTION),
          where('isActive', '==', true)
        );
        const snapshot = await getDocs(q);
        const integrations = snapshot.docs.map(doc => this.docToIntegration(doc.id, doc.data()));
        // Sort in memory
        return integrations.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      }
      throw error;
    }
  }

  // ========== Campaign Management ==========

  static async syncCampaignsFromGoogleAds(_accountId: string): Promise<void> {
    // This would call Google Ads API to fetch campaigns
    // For now, we'll provide the structure - actual API implementation would go here
    // See: https://developers.google.com/google-ads/api/docs/start
    
    // Example API call structure:
    // const campaigns = await this.callGoogleAdsAPI(_accountId, 'campaigns', 'get');
    // for (const campaign of campaigns) {
    //   await this.saveCampaign(campaign);
    // }
    
    throw new Error('Google Ads API integration requires OAuth setup and API calls. See setup documentation.');
  }

  static async createCampaign(campaignData: Partial<GoogleAdsCampaign>): Promise<string> {
    // Call Cloud Function to create campaign via Google Ads API
    const { httpsCallable } = await import('firebase/functions');
    const { functions } = await import('@/config/firebase');
    
    try {
      console.log('[GoogleAdsService] Creating campaign via API...', {
        accountId: campaignData.accountId,
        name: campaignData.name,
        type: campaignData.type,
      });

      const createCampaign = httpsCallable(functions, 'createGoogleAdsCampaign');
      
      const result = await createCampaign({
        accountId: campaignData.accountId || '',
        campaignName: campaignData.name || '',
        budgetAmount: campaignData.budget?.amount || 0,
        budgetPeriod: campaignData.budget?.period || 'DAILY',
        startDate: campaignData.startDate?.toISOString().split('T')[0],
        endDate: campaignData.endDate?.toISOString().split('T')[0],
        campaignType: campaignData.type === GoogleAdsCampaignType.DEMAND_GEN ? 'DEMAND_GEN' :
                     campaignData.type === GoogleAdsCampaignType.VIDEO ? 'VIDEO' :
                     campaignData.type === GoogleAdsCampaignType.SEARCH ? 'SEARCH' : 'DISPLAY',
        audienceSignals: campaignData.demandGenSettings?.audienceSignals || [],
        creativeAssets: campaignData.demandGenSettings?.creativeAssets || [],
        deliveryMethod: campaignData.budget?.deliveryMethod || 'STANDARD',
      });

      const data = result.data as any;
      console.log('[GoogleAdsService] Campaign created:', data);

      if (data.success && data.campaignId) {
        return data.campaignId;
      } else {
        throw new Error(data.message || 'Failed to create campaign');
      }
    } catch (error: any) {
      console.error('[GoogleAdsService] Error creating campaign via API:', error);
      // Fallback to local storage if API fails
      console.warn('[GoogleAdsService] Falling back to local storage...');
      
      const now = Timestamp.now();
      const data: any = {
        accountId: campaignData.accountId || '',
        name: campaignData.name || '',
        type: campaignData.type || GoogleAdsCampaignType.SEARCH,
        status: GoogleAdsCampaignStatus.PAUSED, // Start paused until synced
        budget: campaignData.budget || null,
        targetAudience: campaignData.targetAudience || null,
        startDate: campaignData.startDate ? Timestamp.fromDate(campaignData.startDate) : null,
        endDate: campaignData.endDate ? Timestamp.fromDate(campaignData.endDate) : null,
        metrics: campaignData.metrics || null,
        isDemandGen: campaignData.isDemandGen ?? false,
        demandGenSettings: campaignData.demandGenSettings || null,
        videoCampaignSettings: campaignData.videoCampaignSettings || null,
        createdAt: now,
        lastUpdated: now,
        lastSyncedAt: null,
        needsApiSync: true, // Flag to indicate this needs to be synced
      };

      const docRef = await addDoc(collection(db, GOOGLE_ADS_CAMPAIGNS_COLLECTION), data);
      return docRef.id;
    }
  }

  static async getAllCampaigns(accountId?: string): Promise<GoogleAdsCampaign[]> {
    let q;
    if (accountId) {
      q = query(
        collection(db, GOOGLE_ADS_CAMPAIGNS_COLLECTION),
        where('accountId', '==', accountId),
        orderBy('lastUpdated', 'desc')
      );
    } else {
      q = query(
        collection(db, GOOGLE_ADS_CAMPAIGNS_COLLECTION),
        orderBy('lastUpdated', 'desc')
      );
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToCampaign(doc.id, doc.data()));
  }

  static async getCampaignById(id: string): Promise<GoogleAdsCampaign | null> {
    const docRef = doc(db, GOOGLE_ADS_CAMPAIGNS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToCampaign(docSnap.id, docSnap.data());
  }

  static async updateCampaign(id: string, updates: Partial<GoogleAdsCampaign>): Promise<void> {
    const docRef = doc(db, GOOGLE_ADS_CAMPAIGNS_COLLECTION, id);
    const updateData: any = {
      lastUpdated: Timestamp.now(),
    };

    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.budget !== undefined) updateData.budget = updates.budget;
    if (updates.metrics !== undefined) updateData.metrics = updates.metrics;

    await updateDoc(docRef, updateData);
  }

  // ========== Campaign Metrics Sync ==========

  static async syncCampaignMetrics(campaignId: string, metrics: GoogleAdsCampaignMetrics): Promise<void> {
    await this.updateCampaign(campaignId, { metrics });
  }

  // ========== YouTube Ads (Video Campaigns) ==========

  static async createYouTubeCampaign(campaignData: Partial<GoogleAdsCampaign>): Promise<string> {
    const videoCampaignData = {
      ...campaignData,
      type: GoogleAdsCampaignType.VIDEO,
      videoCampaignSettings: campaignData.videoCampaignSettings || {
        videoIds: [],
        placementTargets: [],
      },
    };
    return this.createCampaign(videoCampaignData);
  }

  // ========== Demand Gen Campaigns ==========

  static async createDemandGenCampaign(campaignData: Partial<GoogleAdsCampaign>): Promise<string> {
    const demandGenData = {
      ...campaignData,
      type: GoogleAdsCampaignType.DEMAND_GEN,
      isDemandGen: true,
      demandGenSettings: campaignData.demandGenSettings || {
        audienceSignals: [],
        creativeAssets: [],
      },
    };
    return this.createCampaign(demandGenData);
  }

  // ========== Helper Methods ==========

  private static docToIntegration(id: string, data: any): GoogleAdsIntegration {
    return {
      id,
      accountId: data.accountId || '',
      accountName: data.accountName || '',
      refreshToken: data.refreshToken,
      accessToken: data.accessToken,
      tokenExpiresAt: data.tokenExpiresAt?.toDate(),
      isActive: data.isActive ?? true,
      lastSyncedAt: data.lastSyncedAt?.toDate(),
      syncFrequency: data.syncFrequency || 'daily',
      createdBy: data.createdBy || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }

  private static docToCampaign(id: string, data: any): GoogleAdsCampaign {
    return {
      id,
      accountId: data.accountId || '',
      name: data.name || '',
      type: data.type || GoogleAdsCampaignType.SEARCH,
      status: data.status || GoogleAdsCampaignStatus.ENABLED,
      budget: data.budget,
      targetAudience: data.targetAudience,
      startDate: data.startDate?.toDate(),
      endDate: data.endDate?.toDate(),
      metrics: data.metrics,
      isDemandGen: data.isDemandGen ?? false,
      demandGenSettings: data.demandGenSettings,
      videoCampaignSettings: data.videoCampaignSettings,
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
      lastSyncedAt: data.lastSyncedAt?.toDate(),
    };
  }

  // ========== Google Ads API Helper Methods ==========
  // These would be implemented to actually call Google Ads API
  // See: https://developers.google.com/google-ads/api/docs/start

  /**
   * Note: Actual Google Ads API integration requires:
   * 1. OAuth 2.0 setup with Google Cloud Console
   * 2. Google Ads API access enabled
   * 3. Customer ID and developer token
   * 4. Proper authentication flow
   * 
   * For production, implement actual API calls using:
   * - google-ads-api npm package
   * - Or direct REST API calls
   */
  
  static async callGoogleAdsAPI(
    _accountId: string,
    _resource: string,
    _method: 'get' | 'mutate',
    _params?: any
  ): Promise<any> {
    // Placeholder - actual implementation would make API calls
    throw new Error('Google Ads API calls require full OAuth setup. See setup documentation.');
  }
}

